import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Card, Button, SeverityPill, fmt$, fmtN } from "@/components/primitives";

const OWN_LABEL = {
  rented: "rented", financed: "financed", owned: "owned",
  unknown: "unknown", contracted: "contracted", sold: "sold", mixed: "mixed",
};
const STATUS_LABEL = {
  stopped: "stopped ordering",
  never: "never ordered",
  unknown: "no orders in available history",
};

function Stat({ label, value, sub, color, basis = 190 }) {
  return (
    <div style={{ flex: `0 1 ${basis}px`, minWidth: 150 }}>
      <div className="prose-i" style={{ fontSize: 11, color: "var(--ui-text-lo)", textTransform: "uppercase", letterSpacing: ".08em" }}>{label}</div>
      <div className="num" style={{ fontSize: 26, fontWeight: 700, color: color || "var(--ui-text-hi)", lineHeight: 1.1, marginTop: 6 }}>{value}</div>
      {sub && <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// Bean volume by month. The bar is the site's own kg; a dot marks a month in
// which non-bean consumables were ordered, which is what "active but dry"
// means - still buying, just not beans.
function VolumeSeries({ series }) {
  const recent = (series || []).slice(-18);
  const max = Math.max(1, ...recent.map((r) => r.kg));
  if (!recent.length) {
    return <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)" }}>No invoiced history for this site.</div>;
  }
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 132, overflowX: "auto", paddingTop: 8, justifyContent: "flex-start" }}>
      {recent.map((r) => (
        <div key={r.period} style={{ flex: "0 0 34px", width: 34, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div className="num" style={{ fontSize: 9.5, color: "var(--ui-text-lo)" }}>{r.kg > 0 ? fmtN(r.kg) : ""}</div>
          <div title={`${r.period}: ${r.kg} kg, ${r.nonBeanOrders} non-bean order(s)`}
            style={{
              width: "100%",
              height: Math.max(2, Math.round((r.kg / max) * 74)),
              background: r.kg > 0 ? "var(--ac-indigo)" : "var(--ui-line-soft)",
              borderRadius: "3px 3px 0 0",
            }} />
          <div style={{ height: 6 }}>
            {r.nonBeanOrders > 0 && (
              <div title={`${r.nonBeanOrders} non-bean consumable order(s)`}
                style={{ width: 6, height: 6, borderRadius: 3, background: "var(--sv-watch)" }} />
            )}
          </div>
          <div className="prose-i" style={{ fontSize: 9, color: "var(--ui-text-lo)", whiteSpace: "nowrap" }}>{r.period.slice(2)}</div>
        </div>
      ))}
    </div>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["site", id],
    queryFn: () => endpoints.site(id),
  });

  if (isLoading) {
    return <div className="prose-i" style={{ color: "var(--ui-text-mid)" }}>Loading site…</div>;
  }
  if (error) {
    return (
      <div style={{ maxWidth: 1400 }}>
        <Card>
          <div style={{ fontWeight: 600, color: "var(--ui-text-hi)" }}>Site not found</div>
          <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)", marginTop: 6 }}>
            No site with id {id}. It may have been closed or relocated in Simpro.
          </div>
          <div style={{ marginTop: 14 }}><Button variant="secondary" onClick={() => nav("/sites")}>← All sites</Button></div>
        </Card>
      </div>
    );
  }

  const { site, company, volume, baseline, signal } = data;
  const eb = signal?.exposureBasis || {};
  const committed = baseline.committedKgPerPeriodSite;

  return (
    <div style={{ maxWidth: 1400, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={() => nav("/")}>← Leak Board</Button>
        <button onClick={() => nav(`/accounts/${company.customerId}`)} className="prose-i"
          style={{ fontSize: 12.5, color: "var(--ac-indigo)", background: "none", border: "none", cursor: "pointer" }}>
          {company.name || "company"} →
        </button>
      </div>

      <Card>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap", justifyContent: "space-between" }}>
          <div style={{ flex: "1 1 320px", minWidth: 260 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: "var(--ui-text-hi)", lineHeight: 1.15 }}>
              {site.siteName || "—"}
            </div>
            <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-mid)", marginTop: 5 }}>
              {company.name} · {OWN_LABEL[site.ownership] || site.ownership || "unknown"}
              {site.ownershipSource ? ` (${site.ownershipSource})` : ""} · {site.machineCount} machine{site.machineCount === 1 ? "" : "s"}
              {site.archived ? " · closed / relocated" : ""}
            </div>
          </div>
          {signal
            ? <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "0 0 auto" }}>
                <SeverityPill severity={signal.severity} label={signal.typeLabel} />
                <div style={{ textAlign: "right" }}>
                  <div className="num" style={{ fontSize: 26, fontWeight: 700, color: "var(--ui-text-hi)" }}>{fmt$(signal.valueAtRisk)}</div>
                  <div className="prose-i" style={{ fontSize: 10.5, color: "var(--ui-text-lo)" }}>annualised exposure</div>
                </div>
              </div>
            : <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", flex: "0 0 auto",
                  border: "1px solid var(--ui-line-soft)", borderRadius: 999, padding: "5px 12px", whiteSpace: "nowrap" }}>
                {volume.everOrderedBeans ? "no signal on this site" : "never ordered beans"}
              </div>}
        </div>

        {signal?.evidence?.headline && (
          <div className="prose-i" style={{ fontSize: 13.5, color: "var(--ui-text-mid)", marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--ui-line-soft)" }}>
            {signal.evidence.headline}
          </div>
        )}
        {eb.text && (
          <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 6 }}>{eb.text}</div>
        )}
      </Card>

      <Card>
        <div style={{ display: "flex", gap: 30, flexWrap: "wrap" }}>
          <Stat label="Baseline" value={baseline.expectedMonthlyKg != null ? `${fmtN(baseline.expectedMonthlyKg)} kg` : "—"}
            sub={baseline.source === "none" ? "no valid baseline" : `from ${baseline.source}`} />
          <Stat label="This month" value={volume.currentPeriodKg != null ? `${fmtN(volume.currentPeriodKg)} kg` : "—"}
            sub="month to date" />
          <Stat label="Days dry" value={volume.daysDry ?? "—"}
            sub={volume.lastBeanOrderDate ? `last bean order ${String(volume.lastBeanOrderDate).slice(0, 10)}` : "no bean order on record"}
            color={volume.daysDry != null && volume.daysDry > 60 ? "var(--sv-crit)" : undefined} />
          <Stat label="All-time volume" value={`${fmtN(volume.kgAllTime)} kg`}
            sub={STATUS_LABEL[volume.orderStatus] || volume.orderStatus || "—"} />
          <Stat label="Reorder interval" value={volume.medianReorderIntervalDays ? `${fmtN(volume.medianReorderIntervalDays)} d` : "—"}
            sub="median, from bean orders" />
        </div>
      </Card>

      <Card>
        <div style={{ fontWeight: 600, fontSize: 15, color: "var(--ui-text-hi)" }}>Bean volume by month</div>
        <div className="prose-i" style={{ fontSize: 12, color: "var(--ui-text-lo)", marginTop: 3 }}>
          Bars are this site's own kg. An amber dot marks a month with non-bean consumable orders — buying, but not beans.
        </div>
        <VolumeSeries series={volume.series} />
      </Card>

      <Card>
        <div style={{ fontWeight: 600, fontSize: 15, color: "var(--ui-text-hi)" }}>Committed volume</div>
        <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 10 }}>
          <Stat basis={260} label="This site" value={committed != null ? `${fmtN(committed)} kg` : "not set"}
            sub={committed != null ? "overrides the history baseline" : "baselined on its own history"} />
          <Stat basis={260} label="Account total" value={baseline.committedKgPerPeriodAccount != null ? `${fmtN(baseline.committedKgPerPeriodAccount)} kg` : "not set"}
            sub="company-wide — not per-site truth" />
        </div>
        <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)", marginTop: 10 }}>
          A committed volume replaces the history baseline, so it moves exposure in whichever direction it differs.
          It is never split across sites — 50 kg across five sites is not 10 kg each.
        </div>
      </Card>

      <Card>
        <div style={{ fontWeight: 600, fontSize: 15, color: "var(--ui-text-hi)" }}>
          Machines <span className="prose-i" style={{ fontSize: 12, fontWeight: 400, color: "var(--ui-text-lo)" }}>· {site.machineCount}</span>
        </div>
        {(site.machines || []).length === 0
          ? <div className="prose-i" style={{ fontSize: 13, color: "var(--ui-text-lo)", marginTop: 8 }}>
              No machine records in Simpro for this site.
            </div>
          : <div style={{ marginTop: 10 }}>
              {site.machines.map((m, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "10px 0", borderTop: i ? "1px solid var(--ui-line-soft)" : "none", flexWrap: "wrap", alignItems: "baseline" }}>
                  <div style={{ flex: "0 1 300px", minWidth: 200, fontSize: 13, color: "var(--ui-text-hi)" }}>
                    {m.makeModel || "—"}
                    {m.location && <span className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}> · {m.location}</span>}
                  </div>
                  <div className="prose-i" style={{ flex: "0 0 200px", fontSize: 12, color: "var(--ui-text-lo)" }}>{m.serialNo ? `serial ${m.serialNo}` : "no serial"}</div>
                  <div className="prose-i" style={{ flex: "0 0 210px", fontSize: 12, color: m.contractType ? "var(--ui-text-mid)" : "var(--sv-watch)" }}>
                    {m.contractType || "no Contract Type in Simpro"}
                  </div>
                  <div className="prose-i" style={{ flex: "0 0 150px", fontSize: 12, color: "var(--ui-text-lo)" }}>{m.installDate ? `installed ${String(m.installDate).slice(0, 10)}` : "no install date"}</div>
                </div>
              ))}
            </div>}
      </Card>

      <div className="prose-i" style={{ fontSize: 11.5, color: "var(--ui-text-lo)" }}>
        Site {site.siteId} · assessed as at {data.reportingDate}
        {volume.historyDays ? ` · ${fmtN(volume.historyDays)} days of history` : ""}
        {volume.dataState ? ` · ${volume.dataState}` : ""}
      </div>
    </div>
  );
}
